import type { AnnotationShape } from '../types';
import { rasterizeAnnotation } from './rasterize';

export interface FrameExportOptions {
  format?: 'image/png' | 'image/jpeg';
  quality?: number;
  annotations?: AnnotationShape[];
}

/**
 * Captures the current video frame as a Blob.
 * Optionally composites annotation shapes over the frame.
 */
export async function captureFrame(
  video: HTMLVideoElement,
  options?: FrameExportOptions,
): Promise<Blob> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) {
    throw new Error('Video has no dimensions');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  ctx.drawImage(video, 0, 0, width, height);

  if (options?.annotations && options.annotations.length > 0) {
    const annotationCanvas = await rasterizeAnnotation(options.annotations, width, height);
    ctx.drawImage(annotationCanvas, 0, 0);
  }

  const format = options?.format ?? 'image/png';
  const quality = options?.quality;

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      format,
      quality,
    );
  });
}

/**
 * Captures the current video frame as a data URL string.
 * Optionally composites annotation shapes over the frame.
 */
export async function captureFrameAsDataUrl(
  video: HTMLVideoElement,
  options?: FrameExportOptions,
): Promise<string> {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) {
    throw new Error('Video has no dimensions');
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Failed to get canvas 2d context');
  }

  ctx.drawImage(video, 0, 0, width, height);

  if (options?.annotations && options.annotations.length > 0) {
    const annotationCanvas = await rasterizeAnnotation(options.annotations, width, height);
    ctx.drawImage(annotationCanvas, 0, 0);
  }

  const format = options?.format ?? 'image/png';
  const quality = options?.quality;

  return canvas.toDataURL(format, quality);
}
